import * as React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { feedbackVariantColors } from '../../shared/styleTokens';
import { renderFeedbackIcon } from '../shared/renderFeedbackIcon';

/**
 * Alert variant options based on semantic meaning
 * - success: Positive feedback (action completed, saved)
 * - warning: Caution states (pending action, review needed)
 * - error: Negative feedback (validation errors, failures)
 * - info: Neutral information (tips, notifications)
 */
export type AlertVariant = 'success' | 'warning' | 'error' | 'info';

/**
 * Base props for the Alert component
 */
interface AlertBaseProps {
  /**
   * The visual variant of the alert
   * @default 'info'
   */
  variant?: AlertVariant;
  /**
   * Optional title displayed prominently above the message
   */
  title?: string;
  /**
   * Custom icon to display. If not provided, uses default icon per variant.
   * Set to null to hide the icon entirely.
   */
  icon?: React.ReactNode | null;
  /**
   * Whether the alert can be dismissed with a close button
   * @default false
   */
  dismissible?: boolean;
  /**
   * Callback when the dismiss button is clicked
   */
  onDismiss?: () => void;
  /**
   * The message content of the alert
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * AlertProps extends HTML div attributes
 */
export type AlertProps = AlertBaseProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, keyof AlertBaseProps>;

/**
 * Default icons for each variant
 */
const defaultIcons: Record<AlertVariant, LucideIcon> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

/**
 * Variant styles based on the design system semantic colors (shared with Toast)
 */
const variantStyles: Record<
  AlertVariant,
  { bg: string; text: string; icon: string; border: string }
> = feedbackVariantColors;

/**
 * Alert component for displaying important messages with semantic variants.
 *
 * Designed for insurance industry applications where clear communication
 * of status, errors, and important information is critical.
 *
 * Features:
 * - Four semantic variants (success, warning, error, info)
 * - Optional title and message content
 * - Default icons per variant (customizable)
 * - Dismissible with close button
 * - 4px left border accent in semantic color
 * - role="alert" for screen reader live region
 *
 * @example
 * // Basic usage with message only
 * <Alert variant="info">
 *   Your session will expire in 5 minutes.
 * </Alert>
 *
 * @example
 * // With title and message
 * <Alert variant="success" title="Policy Created">
 *   Policy POL-2024-001842 has been successfully created.
 * </Alert>
 *
 * @example
 * // Dismissible alert
 * <Alert
 *   variant="warning"
 *   title="Pending Review"
 *   dismissible
 *   onDismiss={() => console.log('dismissed')}
 * >
 *   This claim requires additional documentation before processing.
 * </Alert>
 *
 * @example
 * // Custom icon
 * import { Shield } from 'lucide-react';
 * <Alert variant="info" icon={<Icon icon={Shield} />}>
 *   Your account is protected by two-factor authentication.
 * </Alert>
 *
 * @example
 * // No icon
 * <Alert variant="info" icon={null}>
 *   A simple message without an icon.
 * </Alert>
 */
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      variant = 'info',
      title,
      icon,
      dismissible = false,
      onDismiss,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const styles = variantStyles[variant];
    const DefaultIcon = defaultIcons[variant];

    // Combine all classes
    const combinedClasses = [
      // Base styles
      'relative flex gap-3',
      'rounded-lg',
      'p-4',
      // Left border accent (4px)
      'border-l-4',
      styles.border,
      // Background
      styles.bg,
      // Custom classes
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={ref}
        role="alert"
        className={combinedClasses}
        {...props}
      >
        {/* Icon */}
        {renderFeedbackIcon(icon, DefaultIcon, styles.icon)}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h3
              className={`text-sm font-semibold ${styles.text} ${children ? 'mb-1' : ''}`}
            >
              {title}
            </h3>
          )}
          {children && (
            <div className="text-sm text-secondary-600">{children}</div>
          )}
        </div>

        {/* Close button */}
        {dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex-shrink-0 p-0.5 rounded hover:bg-black/5 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
            aria-label="Dismiss alert"
          >
            <X
              className="w-5 h-5 text-secondary-400 hover:text-secondary-600"
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';

export { Alert };
